import {BotDifficulty, RoomSettingConstants} from '@colonist/game-base-types'
import {TranslationObject} from '@colonist/i18n'
import {RoomSessionClientState} from '../../../../../shared/RoomSessionClientState'
import {RoomState} from '../../../../../shared/RoomState'
import {UIRoomUserView, UserActionIcon} from './UIRoomUserView'
import {clientI18n} from '../../ClientI18n'
import {socket, uiManagerEvents} from '../../EntryShared'
import {Sound} from '../../Sound'
import {UIUtilsEjsVariables} from '../../UIUtilsEjsVariables'
import {SocketLobbySend} from '../../socket/SocketLobbySend'
import {UIPopupController} from '../../ui_popup/UIPopupController'
import {ViewPopupOptions} from '../../ui_popup/ViewPopupOptions'
import {setElementInnerHTML} from '../../ui_utils/DomUtils'
import {UIBrowserTabController} from '../UIBrowserTabController'
import {ClientUserState} from '../../ClientUserState'
import {LobbyImage} from '@colonist/types'
import {AjaxDataTypes} from '../../../../../shared/AjaxDataTypes'
import {AnalyticEventCategory, AnalyticEventType, GiftMembershipMethod} from '../../../../../shared/entity_datas/EntityDatas'
import {CA} from '../../analytics/Analytics'
import {doActionIfCanGiftMembership, doActionIfLoggedIn} from '../../store/StoreUtils'
import {navigateToPage} from '../../ui_utils/ClientUtils'

export abstract class UIRoomUserList {
    private readonly playerListContainer: HTMLDivElement
    private hostView: UIRoomUserView
    private readonly guestViews: UIRoomUserView[] = []
    private static readonly leaveSoundVolume = 0.6

    constructor(playerListContainer: HTMLDivElement) {
        this.playerListContainer = playerListContainer
    }

    abstract openColorWindowForPlayer(player: RoomSessionClientState | undefined): void
    abstract showUpdateUsernamePopup(): void

    clear() {
        setElementInnerHTML(this.playerListContainer, {key: 'strings:colonistCommon.utils.emptyString'})
        this.guestViews.splice(0, this.guestViews.length)
    }

    load(roomState: RoomState) {
        this.hostView = new UIRoomUserView(this.playerListContainer)
        this.createGuestViews()
        this.update(roomState)
    }

    update(newRoomState: RoomState) {
        this.updateHostView(newRoomState)
        this.updateGuestSessionViews(newRoomState)
        this.updateBotButtonViews(newRoomState)
    }

    // ====================================================================================================================================
    // Views
    // ====================================================================================================================================

    private createGuestViews() {
        for(let i = 1; i < RoomSettingConstants.MAX_PLAYER_COUNT; i += 1) {
            const newView = new UIRoomUserView(this.playerListContainer)
            this.guestViews.push(newView)
        }
    }

    private updateHostView(roomState: RoomState) {
        this.hostView.update(roomState.hostSession)
        this.hostView.updateActionIcons(UIRoomUserList.getActionIconsToDisplay(roomState, roomState.hostSession))
        this.hostView.setUsername(UIRoomUserList.getUsername(roomState, roomState.hostSession))

        const hostClickAction = this.playerClickAction(roomState.hostSession, roomState.hostSession, roomState.isPrivateGame())

        UIRoomUserList.togglePlayerHover(this.hostView, hostClickAction == undefined)

        this.hostView.setOnclick(() => {
            Sound.playSound(Sound.SoundType.Click)
            hostClickAction?.()
        })

        const canEditColor = UIRoomUserList.shouldShowColorSelectorBackground(roomState, roomState.hostSession)
        this.updateColorSelector(canEditColor, this.hostView)
    }

    private updateGuestSessionViews(roomState: RoomState) {
        const guestSessions = roomState.getGuestSessions()
        const amIHost = roomState.hostSession.userSessionId == socket.controller.session.id
        for(const [index, view] of this.guestViews.entries()) {
            const guest = guestSessions[index]
            if(roomState.gameSetting.maxPlayers - 1 < index + 1) view.hideView()
            if(roomState.gameSetting.maxPlayers - 1 >= index + 1) view.showView()
            if(guest == undefined) {
                if(view.isShowingPlayer()) {
                    view.removePlayer()
                    UIBrowserTabController.blinkTab({key: 'strings:roomPage.left', options: {count: roomState.getSessionCount(), maxCount: roomState.gameSetting.maxPlayers}})
                    Sound.playSound(Sound.SoundType.LeaveRoom, UIRoomUserList.leaveSoundVolume)
                }
                continue
            }
            if(!view.isShowingPlayer()) {
                UIBrowserTabController.blinkTab({key: 'strings:roomPage.joined', options: {count: roomState.getSessionCount(), maxCount: roomState.gameSetting.maxPlayers}})
                Sound.playSound(Sound.SoundType.JoinRoom)
            }
            view.update(guest)

            const guestClickAction = this.playerClickAction(roomState.hostSession, guest, roomState.isPrivateGame())

            UIRoomUserList.togglePlayerHover(view, guestClickAction == undefined)

            view.setOnclick(() => {
                Sound.playSound(Sound.SoundType.Click)
                guestClickAction?.()
            })

            const canEditColor = UIRoomUserList.shouldShowColorSelectorBackground(roomState, guest)
            this.updateColorSelector(canEditColor, view)

            view.updateActionIcons(UIRoomUserList.getActionIconsToDisplay(roomState, guest))

            view.setBotDifficultySelectorVisibility(guest.isBot)
            view.setBotDifficultySelectorDisabled(!UIRoomUserList.amIHost(roomState))
            view.setBotDifficultySelectorOnClick((selectedBotDifficulty: BotDifficulty) => {
                SocketLobbySend.clickedRoomSetBotDifficulty({botDifficulty: selectedBotDifficulty, roomSessionId: guest.roomSessionId})
            })

            view.setUsername(UIRoomUserList.getUsername(roomState, guest))
        }
    }

    private updateBotButtonViews(roomState: RoomState) {
        if(roomState.hostSession.userSessionId == socket.controller.session.id) {
            for(const view of this.guestViews) {
                view.displayBotButton()
            }
            return
        }
        for(const view of this.guestViews) {
            view.hideBotButton()
        }
    }

    private updateColorSelector(canEditColor: boolean, userView: UIRoomUserView) {
        userView.setColorOnclick(canEditColor ? () => this.openColorWindowForPlayer(userView.state) : () => undefined)
        userView.toggleColorSelector(canEditColor)
    }

    // ====================================================================================================================================
    // Button Checks
    // ====================================================================================================================================

    private static checkIfClickedOnSelf(playerSessionClickedOn: RoomSessionClientState): boolean {
        return playerSessionClickedOn.userSessionId == socket.controller.session.id
    }

    private static checkIfCurrentSessionIsHost(session: RoomSessionClientState): boolean {
        return socket.controller.session.id == session.userSessionId
    }

    // ====================================================================================================================================
    // Button Actions
    // ====================================================================================================================================

    protected playerClickAction(hostSession: RoomSessionClientState, playerSession: RoomSessionClientState, privateGame: boolean): (() => void) | undefined {
        const hostActionPopup = UIRoomUserList.checkIfCurrentSessionIsHost(hostSession) && !(UIRoomUserList.checkIfClickedOnSelf(playerSession)) && !playerSession.isBot
        const hostKickPopup = UIRoomUserList.checkIfCurrentSessionIsHost(hostSession) && playerSession.isBot
        const goToProfilePage = !UIRoomUserList.checkIfCurrentSessionIsHost(hostSession) && !(UIRoomUserList.checkIfClickedOnSelf(playerSession)) && !playerSession.isBot
        const canGoToProfilePageOnDiscord = UIUtilsEjsVariables.isUserOnDiscord && !privateGame

        if(UIRoomUserList.checkIfClickedOnSelf(playerSession)) return () => doActionIfLoggedIn(() => this.showUpdateUsernamePopup())

        if(hostActionPopup) return () => UIRoomUserList.showHostActionsPopup(playerSession, privateGame)
        if(hostKickPopup) return () => UIRoomUserList.showKickPopup(playerSession)
        if(goToProfilePage && canGoToProfilePageOnDiscord) return () => UIRoomUserList.showViewProfilePopup(playerSession)
        if(goToProfilePage && !UIUtilsEjsVariables.isUserOnDiscord) return () => uiManagerEvents.openProfilePage(playerSession.username)

        return undefined
    }

    private static showHostActionsPopup(playerSession: RoomSessionClientState, privateGame: boolean) {
        const title: TranslationObject = {key: 'strings:roomPage.hostActions.title'}
        const body: TranslationObject = {key: 'strings:roomPage.hostActions.body', options: {username: playerSession.username}}
        const showXButton = !(UIUtilsEjsVariables.isUserOnDiscord && privateGame)

        const viewProfileAction = () => {
            uiManagerEvents.openProfilePage(playerSession.username)
        }
        const kickAction = () => {
            SocketLobbySend.clickedRoomKickPlayer(playerSession.roomSessionId)
        }

        const popupOptions: ViewPopupOptions = {
            showXButton: showXButton,
            xButtonCustomContent: {key: 'strings:roomPage.hostActions.viewProfile'},
            checkButtonCustomContent: {key: 'strings:roomPage.hostActions.kick'},
        }

        UIPopupController.create(title, body, kickAction, viewProfileAction, true, popupOptions)
    }

    private static showKickPopup(playerSession: RoomSessionClientState) {
        const title: TranslationObject = {key: 'strings:roomPage.hostActions.kick'}

        const botBody: TranslationObject = {key: 'strings:roomPage.hostActions.kickBot', options: {botName: playerSession.username}}
        const playerBody: TranslationObject = {key: 'strings:roomPage.hostActions.kickPlayer', options: {username: playerSession.username}}

        const body = playerSession.isBot ? botBody : playerBody

        const kickAction = () => {
            SocketLobbySend.clickedRoomKickPlayer(playerSession.roomSessionId)
        }
        const xAction = () => undefined

        const popupOptions: ViewPopupOptions = {
            showXButton: true,
            showCheckButton: true,
            xButtonCustomContent: {key: 'strings:colonistCommon.buttons.cancel'},
            checkButtonCustomContent: {key: 'strings:roomPage.hostActions.kick'},
        }

        UIPopupController.create(title, body, kickAction, xAction, true, popupOptions)
    }

    private static showViewProfilePopup(playerSession: RoomSessionClientState) {
        const title: TranslationObject = {key: 'strings:popups.viewProfile.title'}
        const body: TranslationObject = {key: 'strings:popups.viewProfile.body', options: {username: playerSession.username}}
        const viewProfileAction = async() => {
            uiManagerEvents.openProfilePage(playerSession.username)
        }
        const xAction = () => undefined

        UIPopupController.create(title, body, viewProfileAction, xAction, true)
    }

    private static shouldShowColorSelectorBackground(roomState: RoomState, session: RoomSessionClientState) {
        if(session.isBot && this.amIHost(roomState)) return true
        return this.isItMe(session)
    }

    private static getActionIconsToDisplay(roomState: RoomState, session: RoomSessionClientState): UserActionIcon[] {
        const actionIcons: UserActionIcon[] = []
        const isCurrentUserSession = this.isItMe(session)
        const isCurrentUserId = this.isItCurrentUserId(session)
        const canGift = !session.isBot && !isCurrentUserSession && !isCurrentUserId && !UIUtilsEjsVariables.isUserOnDiscordMobile

        if(canGift) {
            const giftButtonClickEventData: AjaxDataTypes.AnalyticData = {
                type: AnalyticEventType.ABTestClickAnalytics,
                category: AnalyticEventCategory.GiftMembershipOverlay,
                floatValue: GiftMembershipMethod.Room,
            }
            actionIcons.push({icon: LobbyImage.StoreGiftMembership, action: () => {
                void CA.sendAnalyticEvent(giftButtonClickEventData)
                doActionIfCanGiftMembership(() => navigateToPage(`/store?username=${session.username}#gift`, true), session.username)
            }, tooltip: {key: 'strings:game.tooltips.giftMembership', options: {username: session.username}}})
        }

        if(isCurrentUserSession && UIUtilsEjsVariables.isUserOnDiscord) return actionIcons

        if(isCurrentUserSession) actionIcons.push({icon: LobbyImage.Pencil, tooltip: {key: 'strings:homePage.header.changeUsername.title'}})
        else if(this.amIHost(roomState)) actionIcons.push({icon: LobbyImage.XMark, tooltip: {key: 'strings:game.tooltips.kickPlayer', options: {username: session.username}}})

        return actionIcons
    }

    private static getUsername(roomState: RoomState, session: RoomSessionClientState) {
        if(this.isItMe(session)) return `${session.username} ${clientI18n.t('strings:roomPage.you')}`
        if(this.isItHost(roomState, session)) return `${session.username} ${clientI18n.t('strings:roomPage.host')}`
        return session.username
    }

    private static amIHost(roomState: RoomState) {
        return socket.controller.session.id == roomState.hostSession.userSessionId
    }

    static isItMe(session: RoomSessionClientState) {
        return socket.controller.session.id == session.userSessionId
    }

    private static isItCurrentUserId(session: RoomSessionClientState) {
        return ClientUserState.userState?.id == session.userId
    }

    private static isItHost(roomState: RoomState, session: RoomSessionClientState) {
        return roomState.hostSession.userSessionId == session.userSessionId
    }

    private static togglePlayerHover(player: UIRoomUserView, isDisabled: boolean) {
        if(isDisabled) {
            player.disableHover()
            return
        }
        player.enableHover()
    }
}
